import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { notification } from 'tea-component';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import {
  getDataTime,
  getGggFinanceOverview,
  updateGggFinanceOverview,
} from '@sg/services';
import { getModulesForPage } from '@sg/_config/dataTimeModules';
import type { IGggFinanceProjectRow, IGggFinanceSummary } from '../types';
import {
  getFinanceProjectOrderList,
  type IFinanceProjectOrder,
} from '../utils/projectOrder';

const DEFAULT_STUDIO_NAME = 'Grinding Gear Games';
const DEFAULT_STUDIO_COVER = GameDefault;

const parseMonthValue = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, ['YYYY-MM', 'YYYY-MM-DD'], true);
  return parsed.isValid() ? parsed.startOf('month') : null;
};

export function useLiveFinance() {
  const [selectedMonth, setSelectedMonth] = useState<any>(null);
  const [defaultMonthLoading, setDefaultMonthLoading] = useState<boolean>(true);
  const [monthDateRange, setMonthDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<IGggFinanceSummary | null>(null);
  const [studioCover, setStudioCover] = useState<string>(DEFAULT_STUDIO_COVER);
  const [studioName, setStudioName] = useState<string>(DEFAULT_STUDIO_NAME);
  const [projects, setProjects] = useState<IGggFinanceProjectRow[]>([]);
  const [projectOrderList, setProjectOrderList] = useState<IFinanceProjectOrder[]>(
    [],
  );

  const initialProjectsRef = useRef<IGggFinanceProjectRow[]>([]);
  const initialProjectOrderRef = useRef<IFinanceProjectOrder[]>([]);

  useEffect(() => {
    const fetchDefaultMonth = async () => {
      const fallbackMonth = moment().subtract(1, 'month').startOf('month');
      const modules = getModulesForPage('titles_finance');

      if (modules.length === 0) {
        setMonthDateRange({ start: null, end: null });
        setSelectedMonth(fallbackMonth);
        setDefaultMonthLoading(false);
        return;
      }

      try {
        const response = await getDataTime({ modules });
        if (response.code === 0 && response.data) {
          const { start_date, end_date } = response.data;
          setMonthDateRange({
            start: start_date || null,
            end: end_date || null,
          });
          // Default month follows latest available data month from backend.
          setSelectedMonth(
            parseMonthValue(end_date) || fallbackMonth,
          );
        } else {
          setMonthDateRange({ start: null, end: null });
          setSelectedMonth(fallbackMonth);
        }
      } catch (error) {
        console.error('Failed to fetch finance default month:', error);
        notification.error({
          description: 'An error occurred while fetching maximum available date. Please try again later.',
        });
        setMonthDateRange({ start: null, end: null });
        setSelectedMonth(fallbackMonth);
      } finally {
        setDefaultMonthLoading(false);
      }
    };

    fetchDefaultMonth();
  }, []);

  const disabledMonth = useCallback((date: moment.Moment) => {
    // MonthPicker disabledMonth returns true for enabled months.
    // Only restrict when backend provides start_date; end_date caps the max month.
    if (!monthDateRange.start) {
      return true;
    }

    const minDate = parseMonthValue(monthDateRange.start);
    const maxDate = monthDateRange.end
      ? parseMonthValue(monthDateRange.end)
      : null;

    if (!minDate) {
      return true;
    }

    if (date.isBefore(minDate, 'month')) {
      return false;
    }
    if (maxDate && date.isAfter(maxDate, 'month')) {
      return false;
    }
    return true;
  }, [monthDateRange.end, monthDateRange.start]);

  const fetchOverview = async (monthValue = selectedMonth) => {
    if (!monthValue) {
      return;
    }

    setLoading(true);
    try {
      const resData: any = await getGggFinanceOverview({
        datatime: moment(monthValue).format('YYYY-MM'),
      });

      const projectList = resData?.list || [];
      const orderList = getFinanceProjectOrderList(projectList);
      const firstListItem = projectList[0];

      setSummary(resData?.summary || null);
      // studio_cover is on each list row now; use the first item for the overview header avatar.
      setStudioCover(firstListItem?.studio_cover || DEFAULT_STUDIO_COVER);
      setStudioName(
        firstListItem?.studio || resData?.studio_name || DEFAULT_STUDIO_NAME,
      );
      setProjects(projectList);
      setProjectOrderList(orderList);
      initialProjectsRef.current = projectList;
      initialProjectOrderRef.current = orderList;
    } catch (error) {
      console.error('Failed to fetch GGG finance overview:', error);
      notification.error({
        description:
          error instanceof Error ? error.message : 'Failed to load finance overview',
      });
      setSummary(null);
      setStudioCover(DEFAULT_STUDIO_COVER);
      setStudioName(DEFAULT_STUDIO_NAME);
      setProjects([]);
      setProjectOrderList([]);
      initialProjectsRef.current = [];
      initialProjectOrderRef.current = [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedMonth || defaultMonthLoading) {
      return;
    }

    fetchOverview(selectedMonth);
  }, [selectedMonth, defaultMonthLoading]);

  const handleProjectsReorder = (reorderedProjects: IGggFinanceProjectRow[]) => {
    const updated = (reorderedProjects || []).map((project, index) => ({
      ...project,
      order: index + 1,
    }));

    setProjects([...updated]);
    setProjectOrderList(
      updated.map((project, index) => ({
        game_title: project.game_title || '',
        order: index + 1,
      })).filter(item => item.game_title),
    );
  };

  const handleProjectsReorderCancel = () => {
    setProjects([...initialProjectsRef.current]);
    setProjectOrderList([...initialProjectOrderRef.current]);
  };

  const handleConfirm = async (): Promise<boolean> => {
    const hasOrderChanges =
      JSON.stringify(projectOrderList || []) !==
      JSON.stringify(initialProjectOrderRef.current || []);

    if (!projectOrderList?.length || !hasOrderChanges) {
      return true;
    }

    setLoading(true);
    try {
      const response = await updateGggFinanceOverview({
        orders: projectOrderList,
      });
      if (response?.code !== 0) {
        notification.error({
          description: response?.message || 'Failed to save project order',
        });
        return false;
      }
      await fetchOverview(selectedMonth);
      return true;
    } catch (error) {
      console.error('Failed to save GGG finance overview order:', error);
      notification.error({
        description:
          error instanceof Error ? error.message : 'Failed to save project order',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const captureEditBaseline = (
    projectList?: IGggFinanceProjectRow[],
    orderList?: IFinanceProjectOrder[],
  ) => {
    initialProjectsRef.current = [...(projectList ?? projects)];
    initialProjectOrderRef.current = [...(orderList ?? projectOrderList)];
  };

  return {
    selectedMonth,
    setSelectedMonth,
    disabledMonth,
    summary,
    studioCover,
    studioName,
    projects,
    projectOrderList,
    setProjects,
    setProjectOrderList,
    overviewLoading: loading,
    loading: defaultMonthLoading || loading,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleConfirm,
    captureEditBaseline,
  };
}
